import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ZoomIn, ZoomOut, Save, ArrowLeft, Undo2, Redo2, Eye } from 'lucide-react';
import { loadPdfDocument } from '../lib/pdfLoader';
import { useEditorStore } from '../store/editorStore';
import PdfViewport from '../components/pdf/PdfViewport';
import { FIELD_TYPE_DRAG_MIME } from '../components/overlay/OverlayStage';
import PropertiesPanel from '../components/panels/PropertiesPanel';
import FieldAssignmentModal from '../components/FieldAssignmentModal';
import DocumentPreview from '../components/DocumentPreview';

const FIELD_TYPES = {
  SIGNATURE: 'signature',
  INITIALS: 'initials',
  NAME: 'name',
  EMAIL: 'email',
  TEXT: 'text',
  DATE: 'date',
  CHECKBOX: 'checkbox',
  CHECKBOX_GROUP: 'checkbox-group',
  RADIO: 'radio',
  IMAGE: 'image',
};

// Signer colors for visual distinction
const SIGNER_COLORS = [
  { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-700', light: 'bg-blue-100', hex: '#3B82F6' },
  { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-700', light: 'bg-green-100', hex: '#22C55E' },
  { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-700', light: 'bg-purple-100', hex: '#A855F7' },
  { bg: 'bg-orange-500', border: 'border-orange-500', text: 'text-orange-700', light: 'bg-orange-100', hex: '#F97316' },
  { bg: 'bg-pink-500', border: 'border-pink-500', text: 'text-pink-700', light: 'bg-pink-100', hex: '#EC4899' },
];

const DocumentFieldEditorV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { API, accessToken } = useContext(AuthContext);

  const { envelopeId, docId, fileId, fileName, documentTitle, recipients, signerCount, signers, expiryDays } = location.state || {};

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved'
  const [lastSaved, setLastSaved] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [actualFileId, setActualFileId] = useState(fileId);

  // Signer-aware field placement state
  const [selectedSignerIndex, setSelectedSignerIndex] = useState(0); // 0-based index
  // Local signers/recipients — populated from location.state or fetched from API
  const [localSigners, setLocalSigners] = useState(signers || []);
  const [localRecipients, setLocalRecipients] = useState(recipients || []);
  const hasMultipleSigners = localSigners.length > 1;

  const {
    documentModel,
    setDocumentModel,
    scale,
    zoomIn,
    zoomOut,
    currentPage,
    fields,
    selectedFieldType,
    setSelectedFieldType,
    selectedFieldIds,
    updateField,
    clearSelection,
    undo,
    redo,
    canUndo,
    canRedo,
    resetStore,
    setSelectedSignerEmail,
    setSigners: setStoreSigners,
    loadFields,
  } = useEditorStore();

  // Sync selected signer with store when tab changes or localSigners updates
  useEffect(() => {
    if (localSigners.length > 0 && selectedSignerIndex < localSigners.length) {
      const selectedSigner = localSigners[selectedSignerIndex];
      // Use signerId for field assignment (not email, which may be empty)
      setSelectedSignerEmail(selectedSigner.signerId || `signer_${selectedSignerIndex + 1}`);
    }
  }, [selectedSignerIndex, localSigners, setSelectedSignerEmail]);

  // Initialize signers in store whenever localSigners resolves
  useEffect(() => {
    if (localSigners.length > 0) {
      setStoreSigners(localSigners);
      // Set initial signer using signerId (not email)
      setSelectedSignerEmail(localSigners[0].signerId || 'signer_1');
    }
  }, [localSigners, setStoreSigners, setSelectedSignerEmail]);
  
  // Load PDF document and envelope data
  useEffect(() => {
    const loadEnvelopeAndDocument = async () => {
      try {
        setIsLoading(true);

        // Reset the editor store when loading a new document
        resetStore();
        console.log('🔄 Editor store reset for new document');

        // Re-initialize signers from location.state after reset (normal send flow)
        if (signers && signers.length > 0) {
          setStoreSigners(signers);
          setSelectedSignerEmail(signers[0].signerId || 'signer_1');
          console.log('🔄 Re-initialized signers after reset:', signers[0].signerId || 'signer_1');
        }

        let documentFileId = fileId;

        // If no fileId but envelopeId provided, fetch envelope to get document
        if (!documentFileId && envelopeId) {
          // Get envelope documents to find the fileId
          const docsResponse = await axios.get(`${API}/envelopes/${envelopeId}/docs`, {
            headers: { Authorization: `Bearer ${accessToken}` }
          });

          if (docsResponse.data && docsResponse.data.length > 0) {
            documentFileId = docsResponse.data[0].file_id;
            setActualFileId(documentFileId);
          }
        }

        // Fetch recipients from API when not provided via navigation state
        // (covers Resume from Envelopes list and "Back to Editor" from ReviewAndSend)
        if (envelopeId && (!signers || signers.length === 0)) {
          try {
            const recipientsResponse = await axios.get(`${API}/envelopes/${envelopeId}/recipients`, {
              headers: { Authorization: `Bearer ${accessToken}` }
            });
            const fetchedRecipients = recipientsResponse.data || [];
            const fetchedSigners = fetchedRecipients
              .filter(r => r.role !== 'CC')
              .map((r, idx) => ({
                name: r.display_name || r.name,
                email: r.email,
                role: r.role,
                signerIndex: idx + 1,
                signerId: `signer_${idx + 1}`,
              }));
            setLocalRecipients(fetchedRecipients);
            setLocalSigners(fetchedSigners);
            if (fetchedSigners.length > 0) {
              setStoreSigners(fetchedSigners);
              setSelectedSignerEmail(fetchedSigners[0].signerId || 'signer_1');
              console.log('🔄 Fetched recipients for editor:', fetchedSigners);
            }
          } catch (e) {
            console.warn('Could not fetch recipients for editor:', e);
          }
        }

        // Load existing fields into the store (fixes the previous TODO)
        if (envelopeId) {
          try {
            const fieldsResponse = await axios.get(`${API}/envelopes/${envelopeId}/fields`, {
              headers: { Authorization: `Bearer ${accessToken}` }
            });
            // Convert API shape ({ field_type, rect, properties }) to editor shape ({ type, page, x, y, w, h, props })
            const fieldsData = (fieldsResponse.data || []).map((f) => {
              if (!f.rect) return f;
              const { field_type, rect, properties, envelope_id, ...rest } = f;
              return {
                ...rest,
                type: f.type || field_type,
                page: rect.page ?? 1,
                x: rect.x ?? 0,
                y: rect.y ?? 0,
                w: rect.w ?? 0,
                h: rect.h ?? 0,
                props: f.props || properties || {},
              };
            });
            if (fieldsData.length > 0) {
              loadFields(fieldsData);
              setSaveStatus('saved');
              console.log('📂 Loaded existing fields:', fieldsData.length);
            }
          } catch (e) {
            console.warn('Could not load existing fields:', e);
          }
        }
        
        if (!documentFileId) {
          toast.error('No document found');
          navigate('/send');
          return;
        }
        
        // Fetch PDF from backend
        const response = await axios.get(`${API}/files/${documentFileId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          responseType: 'arraybuffer',
        });
        
        // Load PDF with pdfjs
        const model = await loadPdfDocument(response.data);
        setDocumentModel(model);
        
        // Set PDF URL for preview
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        
        toast.success(`Document loaded: ${model.numPages} pages`);
      } catch (error) {
        console.error('Failed to load document:', error);
        toast.error('Failed to load document');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadEnvelopeAndDocument();
    
    // Cleanup: reset store when component unmounts
    return () => {
      console.log('🧹 Cleaning up editor - resetting store');
      resetStore();
    };
  }, [fileId, envelopeId, accessToken, API, resetStore, signers]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore keyboard shortcuts when typing in input/textarea
      const isTyping = e.target.tagName === 'INPUT' || 
                       e.target.tagName === 'TEXTAREA' || 
                       e.target.isContentEditable;
      
      if (isTyping) return; // Don't handle shortcuts when typing
      
      // Undo/Redo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        if (canUndo()) {
          e.preventDefault();
          undo();
        }
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        if (canRedo()) {
          e.preventDefault();
          redo();
        }
      }
      
      // Delete selected field
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedFieldIds.length > 0) {
          e.preventDefault();
          const { deleteField } = useEditorStore.getState();
          selectedFieldIds.forEach((id) => deleteField(id));
          clearSelection();
        }
      }
      
      // Arrow keys for nudging selected field
      if (selectedFieldIds.length === 1) {
        const field = fields.find((f) => f.id === selectedFieldIds[0]);
        if (!field) return;
        
        const nudge = e.shiftKey ? 0.01 : 0.002; // 1% or 0.2% movement
        
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          updateField(field.id, { x: Math.max(0, field.x - nudge) });
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          updateField(field.id, { x: Math.min(1 - field.w, field.x + nudge) });
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          updateField(field.id, { y: Math.max(0, field.y - nudge) });
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          updateField(field.id, { y: Math.min(1 - field.h, field.y + nudge) });
        }
      }
      
      // Escape to deselect
      if (e.key === 'Escape') {
        clearSelection();
        setSelectedFieldType(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFieldIds, fields, updateField, clearSelection, setSelectedFieldType]);
  
  // Auto-save function
  const saveFieldsToEnvelope = async () => {
    if (!envelopeId || fields.length === 0) return;
    
    try {
      setSaveStatus('saving');
      
      // Add docId to each field before saving
      const fieldsWithDoc = fields.map(field => ({
        ...field,
        doc_id: docId || envelopeId  // Use docId if available, fallback to envelopeId
      }));
      
      console.log('💾 Saving fields to backend:', fieldsWithDoc);
      
      // Save fields to envelope
      await axios.post(
        `${API}/envelopes/${envelopeId}/fields`,
        { fields: fieldsWithDoc },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      setSaveStatus('saved');
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
      setSaveStatus('unsaved');
    }
  };
  
  // Auto-save every 8 seconds (as per spec)
  useEffect(() => {
    if (fields.length === 0) return;
    
    const autoSaveInterval = setInterval(() => {
      saveFieldsToEnvelope();
    }, 8000); // 8 seconds
    
    return () => clearInterval(autoSaveInterval);
  }, [fields, envelopeId]);
  
  // Mark as unsaved when fields change
  useEffect(() => {
    if (fields.length > 0) {
      setSaveStatus('unsaved');
    }
  }, [fields]);
  
  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (saveStatus === 'unsaved' && fields.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveStatus, fields]);
  
  const handleSave = async () => {
    if (fields.length === 0) {
      toast.error('Please add at least one field');
      return;
    }

    if (!envelopeId) {
      toast.error('No envelope ID found');
      return;
    }

    // Validation: at least 1 recipient must exist before auto-sending
    if (localRecipients.length === 0) {
      toast.error('Please add recipients before sending');
      return;
    }

    try {
      setIsSaving(true);

      // Step 1: Save fields to envelope
      await saveFieldsToEnvelope();

      // Step 2: Ensure recipients are saved to the envelope
      // (The envelope may have been created with empty recipients in the SendDoc flow)
      const formattedRecipients = localRecipients.map((r, idx) => ({
        name: r.display_name || r.name,
        email: r.email,
        role: r.role || 'Signer',
        signing_order: r.routing_order || r.signerIndex || idx + 1,
      }));
      await axios.post(
        `${API}/envelopes/${envelopeId}/recipients`,
        { recipients: formattedRecipients },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      // Step 3a: Stamp expires_at on the envelope using the user's chosen expiry
      const days = expiryDays || 30;
      const expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
      await axios.put(
        `${API}/envelopes/${envelopeId}`,
        { expires_at: expiresAt },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      // Step 3b: Auto-send the envelope (trigger signature requests to all recipients)
      await axios.post(
        `${API}/envelopes/${envelopeId}/send`,
        {},
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      // Step 4: Success — redirect to envelope detail page
      toast.success('Sent for signature!');
      navigate(`/envelopes/${envelopeId}`);

    } catch (error) {
      console.error('Save & Send failed:', error);
      const msg = error?.response?.data?.detail || 'Failed to send envelope';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleFieldAssignment = async (assignments) => {
    try {
      setIsSaving(true);
      
      // Update fields with assignments
      const updatedFields = fields.map(field => ({
        ...field,
        type: field.type || field.field_type,  // Ensure type is preserved
        props: {
          ...field.props,
          assignedTo: assignments[field.id] || ''
        }
      }));
      
      // Save updated fields with assignments
      const fieldsWithDoc = updatedFields.map(field => ({
        ...field,
        doc_id: docId || envelopeId
      }));
      
      await axios.post(
        `${API}/envelopes/${envelopeId}/fields`,
        { fields: fieldsWithDoc },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      // Add recipients
      await axios.post(
        `${API}/envelopes/${envelopeId}/recipients`,
        { recipients },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      
      setShowAssignmentModal(false);
      toast.success('Fields assigned successfully! Ready to send.');
      navigate(`/envelopes/${envelopeId}/review`);
      
    } catch (error) {
      console.error('Assignment failed:', error);
      toast.error('Failed to assign fields');
    } finally {
      setIsSaving(false);
    }
  };
  
  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading document...</p>
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      {/* Field Assignment Modal */}
      {showAssignmentModal && (
        <FieldAssignmentModal
          fields={fields}
          recipients={recipients || []}
          onAssign={handleFieldAssignment}
          onCancel={() => setShowAssignmentModal(false)}
        />
      )}
      
      <div className="h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/send', {
                state: {
                  fields,
                  fileId: actualFileId,
                  documentTitle,
                  envelopeId,
                  recipients
                }
              })}>
                <ArrowLeft size={16} className="mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-lg font-semibold">{documentTitle || fileName}</h1>
                <p className="text-sm text-gray-500">
                  Page {currentPage} of {documentModel?.numPages || 0} • {Math.round(scale * 100)}% zoom
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={undo}
                disabled={!canUndo()}
                title="Undo (Ctrl+Z)"
                aria-label="Undo"
              >
                <Undo2 size={16} aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={redo}
                disabled={!canRedo()}
                title="Redo (Ctrl+Y)"
                aria-label="Redo"
              >
                <Redo2 size={16} aria-hidden="true" />
              </Button>
              <div className="w-px h-6 bg-gray-300" />
              <Button variant="outline" size="sm" onClick={zoomOut} aria-label="Zoom out">
                <ZoomOut size={16} aria-hidden="true" />
              </Button>
              <span className="text-sm text-gray-600" aria-live="polite">{Math.round(scale * 100)}%</span>
              <Button variant="outline" size="sm" onClick={zoomIn} aria-label="Zoom in">
                <ZoomIn size={16} aria-hidden="true" />
              </Button>
              {/* Save Status Indicator */}
              {saveStatus === 'saving' && (
                <span className="text-sm text-gray-500">Saving...</span>
              )}
              {saveStatus === 'saved' && lastSaved && (
                <span className="text-sm text-green-600">✓ Saved</span>
              )}
              {saveStatus === 'unsaved' && (
                <span className="text-sm text-amber-600">Unsaved changes</span>
              )}
              {/* Preview button removed - preview only shows in ReviewAndSend page */}
              <Button onClick={handleSave} disabled={isSaving || fields.length === 0}>
                <Save size={16} className="mr-2" />
                {isSaving ? 'Saving...' : 'Save & Continue'}
              </Button>
            </div>
          </div>
        </div>

        {/* Signer Selector Tabs - Only show when multiple signers */}
        {hasMultipleSigners && (
          <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Placing fields for:</span>
              <div className="flex gap-2">
                {localSigners.map((signer, index) => {
                  const color = SIGNER_COLORS[index % SIGNER_COLORS.length];
                  const isSelected = selectedSignerIndex === index;
                  const signerLabel = signer.name || `Signer ${index + 1}`;

                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedSignerIndex(index)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                        isSelected
                          ? `${color.bg} text-white shadow-md`
                          : `${color.light} ${color.text} hover:opacity-80`
                      }`}
                    >
                      <span className={`w-3 h-3 rounded-full ${isSelected ? 'bg-white' : color.bg}`}></span>
                      {signerLabel}
                      <span className="text-xs opacity-75">
                        ({fields.filter(f => f.props?.assignedTo === signer.signerId).length} fields)
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="ml-auto text-xs text-gray-500">
                💡 Fields will be assigned to the selected signer
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Field Palette */}
          <div className="w-64 bg-white border-r border-gray-200 p-4 overflow-y-auto space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Add Fields</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(FIELD_TYPES).map(([key, type]) => (
                  <Button
                    key={type}
                    variant={selectedFieldType === type ? 'default' : 'outline'}
                    className="w-full"
                    onClick={() => setSelectedFieldType(selectedFieldType === type ? null : type)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(FIELD_TYPE_DRAG_MIME, type);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                  >
                    {key}
                  </Button>
                ))}
                {selectedFieldType ? (
                  <p className="text-xs text-gray-500 pt-1">
                    Click the document to place another {selectedFieldType} field. Click the
                    highlighted button again or press Esc to stop.
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 pt-1">
                    Drag a field onto the document, or click a field and then click the document.
                  </p>
                )}
              </CardContent>
            </Card>
            
            {/* Multi-recipient helper */}
            {localRecipients.length >= 2 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
                <p className="font-semibold text-blue-900 mb-1">📝 Agreement Mode</p>
                <p className="text-blue-800">
                  {localRecipients.length} recipients will sign this document. Use the tabs above to switch recipients and place fields for each one.
                </p>
              </div>
            )}
            
            <div className="text-xs text-gray-500 space-y-1">
              <p>• Click a field type, then click on document</p>
              <p>• Drag fields to move</p>
              <p>• Arrow keys to nudge</p>
              <p>• Delete/Backspace to remove</p>
              <p>• {fields.length} fields placed</p>
            </div>
          </div>
          
          {/* Main Content - PDF Viewer */}
          <div className="flex-1 overflow-hidden">
            {documentModel && <PdfViewport documentModel={documentModel} />}
          </div>
          
          {/* Right Sidebar - Properties */}
          <div className="w-64 bg-white border-l border-gray-200 p-4 overflow-y-auto">
            <PropertiesPanel recipients={localRecipients.length > 0 ? localRecipients : (recipients || [])} />
          </div>
        </div>
      </div>
      
      {/* Preview Modal removed - preview now only available in ReviewAndSend page */}
    </Layout>
  );
};

export default DocumentFieldEditorV2;
